import React, { useEffect, useState } from 'react';

import { Box, Typography, Chip } from '@mui/material';
import {
  red100,
  red10,
  gray90,
  primaryColor,
  primary10,
  gray100
} from '../../../constants/Colors';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { common } from '../../../modules/error';
import { getNewsList } from '../../../api/microcms/news';
import { NewsType } from '../../../api/microcms/news';
import { sentryLog } from '../../../config/sentry';

const styles = {
  wrapper: {
    '@media screen and (max-width:600px)': {
      padding: '24px 16px'
    },
    padding: '32px'
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '24px',
      mb: '16px'
    },
    color: gray100,
    fontSize: '32px',
    fontWeight: 500,
    mb: '24px'
  },
  row: {
    width: '100%',
    alignItems: 'center',
    borderBottom: '1px solid #D8D9DB',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontWeight: '500',
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0.4px',
    color: gray100,
    padding: '24px 0px',
    textDecoration: 'none',
    '@media screen and (max-width:600px)': {
      padding: '16px 0'
    }
  },
  tag: {
    borderRadius: '26px',
    fontSize: '12px',
    fontWeight: 500,
    height: '22px',
    mr: '8px'
  },
  date: {
    '@media screen and (max-width:600px)': {
      fontSize: '14px'
    },
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '22px',
    color: gray90,
    mr: '16px'
  },
  tabs: {
    borderBottom: '1px solid #DBDBE0'
  },
  tab: {
    '@media screen and (max-width:600px)': {
      fontSize: '14px'
    },
    '&.Mui-selected': {
      color: primaryColor
    },
    padding: '9px 16px',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '0.4px'
  },
  tags: {
    display: 'flex',
    flexDirection: 'row',
    rowGap: '8px',
    mr: '8px'
  },
  body: {
    '@media screen and (max-width:600px)': {
      flexDirection: 'column',
      rowGap: '8px'
    },
    display: 'flex',
    flexDirection: 'row'
  }
};

interface Props {
  currentPage: number;
  setErrorText: (value: string) => void;
  setLoading: (value: boolean) => void;
  setOpen: (value: boolean) => void;
  setTotalPages: (value: number) => void;
}

const GeneralNewsItem = (news: NewsType): React.ReactElement => {
  const chipColor = (tag: string) => {
    switch (tag) {
      case '重要':
        return { color: red100, backgroundColor: red10 };
      default:
        return { color: primaryColor, backgroundColor: primary10 };
    }
  };

  return (
    <Box
      sx={styles.row}
      component={'a'}
      href={'/news/general/' + news.id}
      target="_blank"
    >
      <Box sx={styles.body}>
        <Box display="flex" flexDirection={'row'}>
          <Typography sx={styles.date}>{news.publishedAt}</Typography>
          <Box sx={styles.tags}>
            {news.important_flg && (
              <Chip
                label={'重要'}
                sx={{ ...chipColor('重要'), ...styles.tag }}
              />
            )}
            <Chip
              label={news.category}
              sx={{ ...chipColor(news.category), ...styles.tag }}
            />
          </Box>
        </Box>
        <Typography>{news.title}</Typography>
      </Box>
      <ChevronRightIcon />
    </Box>
  );
};

const GeneralNewsItemList = (props: Props): React.ReactElement => {
  const { currentPage, setErrorText, setLoading, setOpen, setTotalPages } =
    props;
  const [newsList, setNewsList] = useState<NewsType[]>([]);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const fetchedData = await getNewsList(currentPage);

        const totalPages = Math.ceil(fetchedData.totalCount / 10);
        setTotalPages(totalPages);

        const displayData = fetchedData.contents.map((item: NewsType) => {
          // 日付の表示形式を変更
          const publishedDate = new Date(item.publishedAt);
          const formattedPublishedDate = publishedDate
            .toISOString()
            .slice(0, 10)
            .replace(/-/g, '/');
          return {
            ...item,
            publishedAt: formattedPublishedDate
          };
        });
        setNewsList(displayData);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        setErrorText(common);
        setOpen(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [currentPage]);

  return (
    <>
      {newsList.map((news) => (
        <GeneralNewsItem {...news} key={news.id} />
      ))}
    </>
  );
};

export default GeneralNewsItemList;
